import React, { useState, useMemo, useEffect } from "react";
import styled from "styled-components";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { FaFileExcel } from "react-icons/fa";
import { Tooltip as InfoTooltip } from "react-tooltip";
import { Info } from "lucide-react";

// === Styled Components ===
const ChartTitle = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-weight: bold;
  color: #666;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 16px;
  gap: 6px;
`;

const PageButton = styled.button`
  padding: 6px 12px;
  font-size: 14px;
  border: 1px solid #ccc;
  background-color: ${(props) => (props.active ? "#9848c7" : "#fff")};
  color: ${(props) => (props.active ? "#000" : "#333")};
  cursor: pointer;
  border-radius: 2px;
  min-width: 36px;

  &:disabled { opacity: 0.4; cursor: default; }
  &:hover:not(:disabled) { background-color: ${(props) => (props.active ? "#9848c7" : "#f2f2f2")}; }
`;

const footerStyle = {
  border: "1px solid #ccc",
  padding: "6px 8px",
  textAlign: "center",
  fontWeight: "bold",
  backgroundColor: "#f0f0f0",
};

const InfoIconWrapper = styled.div`
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  &:hover { color: #000; }
  svg { width: 18px; height: 18px; }
`;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
`;

const Select = styled.select`
  padding: 8px 12px;
  font-size: 15px;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const SearchInput = styled.input`
  padding: 8px 12px;
  font-size: 15px;
  border: 1px solid #ccc;
  border-radius: 4px;
  flex-grow: 1;
  max-width: 300px;
`;

const ExportButton = styled.button`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background-color: #1d6f42;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &:hover { background-color: #155e36; }
  svg { margin-right: 6px; }
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid #ccc;
  border-radius: 8px;
  overflow: hidden;
  margin-top: 12px;
  background-color: #fff;
  font-size: 0.7rem;

  th, td {
    border: 1px solid #ddd;
    text-align: center;
    padding: 14px 8px;
    line-height: 2;
  }
  th { background: #dab7f0; }
  tr:nth-child(even) { background-color: #f9f9f9; }
  tr:hover { background-color: #f5e8ff; }

  .positive { background-color: #e7f9ed; color: #2e7d32; font-weight: 500; }
  .negative { background-color: #ffeaea; color: #c62828; font-weight: 500; }

  .highlight-top { background-color: rgba(0, 128, 0, 0.1); font-weight: bold; }
  .highlight-low { background-color: rgba(255, 0, 0, 0.1); font-weight: bold; }
  .highlight-neutral { background-color: #f0f0f0; }

  .rank-up { color: green; font-weight: bold; }
  .rank-down { color: red; font-weight: bold; }
  .rank-neutral { color: #666; }
`;

// === Main Component Start ===
const ZakupyByContractorTable = ({ data }) => {
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null); // number 0..11 or "ALL"
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortKey, setSortKey] = useState("YTD");
  const [sortOrder, setSortOrder] = useState("desc");

  const rowsPerPage = 10;

  const monthNamesPL = [
    "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
    "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień",
  ];

  const latestDate = useMemo(() => {
    const dates = data.map((item) => new Date(item.DataZakupu)).filter((d) => !isNaN(d));
    return dates.length ? new Date(Math.max(...dates)) : new Date();
  }, [data]);

  // Available years
  const availableYears = useMemo(() => {
    const years = new Set();
    data.forEach((item) => {
      const d = new Date(item.DataZakupu);
      if (!isNaN(d)) years.add(d.getFullYear());
    });
    return Array.from(years).sort();
  }, [data]);

  // Default selection: latest year & latest month
  useEffect(() => {
    if (data.length && selectedYear === null && selectedMonth === null && latestDate) {
      const latestYear = latestDate.getFullYear();
      const latestMonth = latestDate.getMonth();
      if (availableYears.includes(latestYear)) {
        setSelectedYear(latestYear);
        setSelectedMonth(latestMonth);
      }
    }
  }, [data, selectedYear, selectedMonth, latestDate, availableYears]);

  const allContractors = useMemo(() => {
    const purchaseMap = {};
    const ytdPrevMap = {};

    const isAll = selectedMonth === "ALL";

    data.forEach((item) => {
      const name = item.NazwaDostawcy;
      const nip = item.NrDostawcy || "Brak NIP";
      const date = new Date(item.DataZakupu);
      const value = parseFloat(item.K_42 || 0); // purchase amount
      if (!name || isNaN(date) || isNaN(value)) return;

      const year = date.getFullYear();
      const month = date.getMonth();
      const id = `${name}-${nip}`;

      if (!purchaseMap[id]) {
        purchaseMap[id] = {
          id,
          name,
          nip,
          YTD: 0,
          currentMonth: 0,
          lastMonth: 0,
          lastYearSameMonth: 0,
        };
      }

      // Current year aggregations
      if (year === selectedYear) {
        purchaseMap[id].YTD += value;

        // For "ALL": currentMonth is whole-year sum (same as YTD)
        if (isAll || month === selectedMonth) {
          purchaseMap[id].currentMonth += value;
        }

        // m/m only if a specific month is selected
        if (!isAll) {
          const prevMonth = selectedMonth === 0 ? 11 : selectedMonth - 1;
          const prevYear = selectedMonth === 0 ? selectedYear - 1 : selectedYear;
          if (month === prevMonth && year === prevYear) {
            purchaseMap[id].lastMonth += value;
          }
        }
      }

      // Previous year aggregations
      if (year === selectedYear - 1) {
        if (!ytdPrevMap[id]) ytdPrevMap[id] = 0;
        ytdPrevMap[id] += value;

        if (isAll) {
          // For "ALL": compare YTD vs YTD (r-1)
          purchaseMap[id].lastYearSameMonth =
            (purchaseMap[id].lastYearSameMonth || 0) + value;
        } else if (month === selectedMonth) {
          // For specific month: compare month vs same month (r-1)
          purchaseMap[id].lastYearSameMonth =
            (purchaseMap[id].lastYearSameMonth || 0) + value;
        }
      }
    });

    let contractors = Object.values(purchaseMap).map((c) => {
      // m/m only when month chosen; r/r always if reference exists
      const momChange =
        selectedMonth !== "ALL" && c.lastMonth > 0
          ? ((c.currentMonth - c.lastMonth) / c.lastMonth) * 100
          : null;

      const yoyChange =
        c.lastYearSameMonth > 0
          ? ((c.currentMonth - c.lastYearSameMonth) / c.lastYearSameMonth) * 100
          : null;

      return { ...c, momChange, yoyChange };
    });

    // Rank current (by YTD)
    contractors.sort((a, b) => b.YTD - a.YTD);
    contractors = contractors.map((c, i) => ({ ...c, rank: i + 1 }));

    // Rank previous year (by YTD r-1)
    const prevRankMap = Object.entries(ytdPrevMap)
      .sort((a, b) => b[1] - a[1])
      .reduce((acc, [key], i) => {
        acc[key] = i + 1;
        return acc;
      }, {});

    contractors = contractors.map((c) => {
      const prevRank = prevRankMap[`${c.name}-${c.nip}`] || null;
      return {
        ...c,
        prevRank,
        rankChange: prevRank != null ? prevRank - c.rank : null,
      };
    });

    return contractors;
  }, [data, selectedYear, selectedMonth]);

  const filtered = useMemo(() => {
    return allContractors.filter(
      (c) =>
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.nip.toLowerCase().includes(search.toLowerCase())
    );
  }, [allContractors, search]);

  const sorted = useMemo(() => {
    const sorted = [...filtered];
    sorted.sort((a, b) => {
      const aVal = a[sortKey] ?? 0;
      const bVal = b[sortKey] ?? 0;
      return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
    });
    return sorted;
  }, [filtered, sortKey, sortOrder]);

  const paginated = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return sorted.slice(start, start + rowsPerPage);
  }, [sorted, currentPage]);

  const pageCount = Math.ceil(filtered.length / rowsPerPage);

  // Summary totals
  const summary = useMemo(() => {
    const sum = {
      YTD: 0,
      currentMonth: 0,
      lastMonth: 0,
      lastYearSameMonth: 0,
    };

    filtered.forEach((c) => {
      sum.YTD += c.YTD;
      sum.currentMonth += c.currentMonth;
      sum.lastMonth += c.lastMonth;
      sum.lastYearSameMonth += c.lastYearSameMonth;
    });

    const avg = {
      YTD: sum.YTD / filtered.length || 0,
      currentMonth: sum.currentMonth / filtered.length || 0,
      lastMonth: sum.lastMonth / filtered.length || 0,
      lastYearSameMonth: sum.lastYearSameMonth / filtered.length || 0,
    };

    return { sum, avg };
  }, [filtered]);

  const format = (val) =>
    val != null
      ? val
          .toFixed(2)
          .replace(".", ",")
          .replace(/\B(?=(\d{3})+(?!\d))/g, " ")
      : "–";

  const formatPercent = (val) =>
    val != null ? `${val.toFixed(1).replace(".", ",")}%` : "–";

  const handleExport = () => {
    const rows = sorted.map((row) => ({
      Nazwa: row.name,
      NIP: row.nip,
      YTD: format(row.YTD),
      "Bieżący miesiąc": format(row.currentMonth),
      "Poprz. miesiąc": format(row.lastMonth),
      "% m/m-1": formatPercent(row.momChange),
      "Ten sam mies. (r-1)": format(row.lastYearSameMonth),
      "% r/r-1": formatPercent(row.yoyChange),
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Zakupy");

    const monthPart =
      selectedMonth === "ALL"
        ? "caly_rok"
        : String((selectedMonth ?? 0) + 1).padStart(2, "0");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });

    saveAs(blob, `zakupy_kontrahenci_${selectedYear}_${monthPart}.xlsx`);
  };

  return (
    <div>
      <ChartTitle>
        Lista wszystkich kontrahentów wg wartości zakupów
        <InfoIconWrapper data-tooltip-id="zakupy-by-contractor-tooltip">
          <Info />
        </InfoIconWrapper>
      </ChartTitle>

      <InfoTooltip
        id="zakupy-by-contractor-tooltip"
        place="left"
        content={
          <>
            <strong>Kontrahenci wg zakupów</strong><br />
            Tabela pokazuje wartość zakupów wg kontrahenta,<br />
            z podziałem na miesiąc / cały rok, m/m, r/r i sumę roczną.
          </>
        }
      />

      {/* Filters */}
      <Controls>
        <Select
          value={selectedYear ?? ""}
          onChange={(e) => {
            setSelectedYear(parseInt(e.target.value));
            setCurrentPage(1);
          }}
        >
          <option value="" disabled>Wybierz rok</option>
          {availableYears.map((year) => (
            <option key={year} value={year}>{year}</option>
          ))}
        </Select>

        <Select
          value={selectedMonth === "ALL" ? "ALL" : (selectedMonth ?? "")}
          onChange={(e) => {
            const val = e.target.value === "ALL" ? "ALL" : parseInt(e.target.value);
            setSelectedMonth(val);
            setCurrentPage(1);
          }}
        >
          <option value="ALL">Cały rok</option>
          {monthNamesPL.map((m, idx) => (
            <option key={idx} value={idx}>{m}</option>
          ))}
        </Select>

        <SearchInput
          type="text"
          placeholder="Szukaj po nazwie lub NIP..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setCurrentPage(1);
          }}
        />

        <ExportButton onClick={handleExport}>
          <FaFileExcel />
          Eksportuj do Excel
        </ExportButton>
      </Controls>

      {/* Table */}
      <div style={{ overflowX: "auto", width: "100%" }}>
        <StyledTable>
          <thead>
            <tr>
              {[
                ["name", "Nazwa"],
                ["currentMonth", selectedMonth === "ALL" ? "Wartość (rok)" : "Wartość bieżący m-c"],
                ["lastMonth", selectedMonth === "ALL" ? "Wartość poprzedni m-c (—)" : "Wartość poprzedni m-c"],
                ["lastYearSameMonth", selectedMonth === "ALL" ? "Wartość r-1 (rok)" : "Wartość r-1"],
                ["momChange", selectedMonth === "ALL" ? "m/m (—)" : "m/m (%)"],
                ["yoyChange", "r/r (%)"],
                ["rank", "Ranking"],
                ["prevRank", "Ranking r-1"],
                ["rankChange", "Zmiana rankingu"],
                ["YTD", "YTD"],
              ].map(([key, label]) => (
                <th
                  key={key}
                  className={key === sortKey ? "sorted" : ""}
                  onClick={() => {
                    if (sortKey === key) {
                      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                    } else {
                      setSortKey(key);
                      setSortOrder("desc");
                    }
                  }}
                >
                  {label} {sortKey === key ? (sortOrder === "asc" ? "▲" : "▼") : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginated.map((c, idx) => (
              <tr key={idx}>
                <td>{c.name}</td>
                <td>{format(c.currentMonth)}</td>
                <td>{selectedMonth === "ALL" ? "–" : format(c.lastMonth)}</td>
                <td>{format(c.lastYearSameMonth)}</td>
                <td className={`bold ${
                  c.momChange > 0 ? "positive" : c.momChange < 0 ? "negative" : ""
                }`}>
                  {selectedMonth === "ALL" ? "–" : formatPercent(c.momChange)}
                </td>
                <td className={`bold ${
                  c.yoyChange > 0 ? "positive" : c.yoyChange < 0 ? "negative" : ""
                }`}>
                  {formatPercent(c.yoyChange)}
                </td>
                <td className={
                  c.rank <= 3 ? "highlight-top" :
                  c.rank >= 8 ? "highlight-low" : "highlight-neutral"
                }>
                  {c.rank}
                </td>
                <td className={
                  c.prevRank != null
                    ? (c.prevRank <= 3 ? "highlight-top" :
                       c.prevRank >= 8 ? "highlight-low" : "highlight-neutral")
                    : "highlight-neutral"
                }>
                  {c.prevRank || "-"}
                </td>
                <td className={
                  c.rankChange > 0 ? "rank-up" :
                  c.rankChange < 0 ? "rank-down" : "rank-neutral"
                }>
                  {c.rankChange !== null
                    ? c.rankChange > 0
                      ? `↑ ${c.rankChange}`
                      : c.rankChange < 0
                      ? `↓ ${Math.abs(c.rankChange)}`
                      : "–"
                    : "-"
                  }
                </td>
                <td>{format(c.YTD)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td style={footerStyle} colSpan={2}>Suma:</td>
              <td style={footerStyle}>{format(summary.sum.currentMonth)}</td>
              <td style={footerStyle}>{format(summary.sum.lastMonth)}</td>
              <td style={footerStyle}>{format(summary.sum.lastYearSameMonth)}</td>
              <td style={footerStyle}>–</td>
              <td style={footerStyle}>–</td>
              <td style={footerStyle}>–</td>
              <td style={footerStyle}>–</td>
              <td style={footerStyle}>{format(summary.sum.YTD)}</td>
            </tr>
            <tr>
              <td style={footerStyle} colSpan={2}>Średnia:</td>
              <td style={footerStyle}>{format(summary.avg.currentMonth)}</td>
              <td style={footerStyle}>{format(summary.avg.lastMonth)}</td>
              <td style={footerStyle}>{format(summary.avg.lastYearSameMonth)}</td>
              <td style={footerStyle}>–</td>
              <td style={footerStyle}>–</td>
              <td style={footerStyle}>–</td>
              <td style={footerStyle}>–</td>
              <td style={footerStyle}>{format(summary.avg.YTD)}</td>
            </tr>
          </tfoot>
        </StyledTable>
      </div>

      {/* Pagination */}
      <div style={{ marginTop: "10px", textAlign: "center" }}>
        {pageCount > 1 && (
          <PaginationWrapper>
            <PageButton
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
            >
              «
            </PageButton>

            {Array.from({ length: Math.min(5, pageCount) }, (_, i) => {
              let page = i + 1;
              if (currentPage > 3 && currentPage < pageCount - 1) {
                page = currentPage - 2 + i;
              } else if (currentPage >= pageCount - 1 && pageCount > 5) {
                page = pageCount - 4 + i;
              }
              if (page < 1 || page > pageCount) return null;

              return (
                <PageButton
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  active={page === currentPage}
                >
                  {page}
                </PageButton>
              );
            })}

            <PageButton
              onClick={() => setCurrentPage((p) => Math.min(p + 1, pageCount))}
              disabled={currentPage === pageCount}
            >
              »
            </PageButton>
          </PaginationWrapper>
        )}
      </div>
    </div>
  );
};

export default ZakupyByContractorTable;
